import React from "react";
import { Link } from "react-router-dom";
import { Github, Twitter, Twitch, Youtube } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const cols = [
  {
    title: "Platform",
    links: [
      { label: "Browse", to: "/browse" },
      { label: "Games", to: "/games" },
      { label: "LFG", to: "/lfg" },
      { label: "Shop", to: "/shop" },
      { label: "Tournaments", to: "/tournaments" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Careers", to: "/careers" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    title: "Community",
    links: [
      { label: "Community Guidelines", to: "/community" },
      { label: "Trust & Safety", to: "/safety" },
      { label: "Creators", to: "/signup" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Terms of Service", to: "/terms" },
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Cookie Policy", to: "/cookies" },
      { label: "Refunds & Payouts", to: "/refunds" },
      { label: "DMCA", to: "/dmca" },
    ],
  },
];

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      className="relative overflow-hidden border-t border-white/5 bg-[#0A0A0C] mt-16"
      data-testid="site-footer"
    >
      <div className="cyber-grid opacity-30" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8">
          <div className="col-span-2 sm:col-span-3 lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-2.5" data-testid="footer-logo">
              <LogoMark size={40} />
              <div className="leading-none">
                <div className="pixel-title text-2xl">
                  <span style={{ color: "var(--sc-pink)" }} className="text-glow-pink">Mysti</span>
                  <span style={{ color: "var(--sc-volt)", textShadow: "0 0 12px var(--sc-volt)" }}>Cast</span>
                </div>
                <div className="sc-mono text-[9px] tracking-[0.28em] mt-1.5">
                  <span style={{ color: "var(--sc-volt)" }}>STREAM.</span>{" "}
                  <span style={{ color: "var(--sc-pink)" }}>CONNECT.</span>{" "}
                  <span style={{ color: "var(--sc-cyan)" }}>PLAY.</span>
                </div>
              </div>
            </Link>
            <p className="text-xs text-zinc-500 mt-3 max-w-xs leading-relaxed">
              Where creators build communities. Streaming, games, and a room worth returning to.
            </p>
            <div className="flex items-center gap-3 mt-4">
              <a href="https://twitter.com" target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-[color:var(--sc-cyan)] transition" data-testid="footer-social-twitter" aria-label="Twitter">
                <Twitter className="w-4 h-4" />
              </a>
              <a href="https://github.com" target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-[color:var(--sc-volt)] transition" data-testid="footer-social-github" aria-label="GitHub">
                <Github className="w-4 h-4" />
              </a>
              <a href="https://twitch.tv" target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-[color:var(--sc-pink)] transition" data-testid="footer-social-twitch" aria-label="Twitch">
                <Twitch className="w-4 h-4" />
              </a>
              <a href="https://youtube.com" target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-[color:var(--sc-orange)] transition" data-testid="footer-social-youtube" aria-label="YouTube">
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>

          {cols.map((c) => (
            <div key={c.title}>
              <div className="sc-label mb-4 text-[color:var(--sc-cyan)]">{c.title}</div>
              <ul className="space-y-2">
                {c.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="text-xs text-zinc-400 hover:text-white transition"
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-white/5 flex flex-wrap items-center justify-between gap-3">
          <div className="sc-mono text-[10px] text-zinc-500">
            &copy; {year} MystiCast &middot; Where creators build communities.
          </div>
          <div className="flex items-center gap-2 sc-mono text-[10px] text-zinc-500">
            <span className="chip-neon pink">18+ ONLY</span>
            <span className="chip-neon volt">NO GAMBLING</span>
            <span className="chip-neon cyan">STRIPE VERIFIED</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
